// Updated: WhatsApp link now uses the main number +31 85 02 33 564.
/* Klantenservice dynamic page - FAQ-like customer service pages
 * Converted from Next.js to Vite/React
 * Features: FAQ accordion layout with questions/answers and contact CTAs
 * Updated: Markdown rendering for FAQ answers using marked library
 * Updated: Restyled to Buitn Planten design system (warm neutrals, bosgroen accent)
 * Updated: Unified layout to match BlogsPage - ContactBar, bg-white, max-w-7xl, jv-primary headings
 * Updated: Content uses full container width (max-w-7xl), WhatsApp uses 06 number, final FAQ item hides border-b
 * Updated: Afhalen and Bezorging pages show the AFHALEN20 pickup offer. */

import { sanitizeHtml } from '@/lib/sanitize-html';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Mail, Phone, TicketPercent, Loader as Loader2 } from 'lucide-react';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { supabase } from '@/lib/supabase';
import { marked } from 'marked';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface KlantenservicePageData {
  id: string;
  title: string;
  slug: string;
  description?: string;
  meta_title?: string;
  meta_description?: string;
  active: boolean;
  display_order: number;
}

interface FAQ {
  id: string;
  page_id: string;
  question: string;
  answer: string;
  active: boolean;
  display_order: number;
}

export default function KlantenservicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState<KlantenservicePageData | null>(null);
  const [faqs, setFaqs] = useState<FAQ[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPageAndFaqs = async () => {
      if (!slug) return;

      try {
        setLoading(true);

        const { data: pageData, error: pageError } = await supabase
          .from('klantenservice_pages')
          .select('*')
          .eq('slug', slug)
          .eq('active', true)
          .maybeSingle();

        if (pageError) throw pageError;

        if (!pageData) {
          navigate('/');
          return;
        }

        setPage(pageData);

        const { data: faqData, error: faqError } = await supabase
          .from('klantenservice_faqs')
          .select('*')
          .eq('page_id', pageData.id)
          .eq('active', true)
          .order('display_order');

        if (faqError) throw faqError;

        setFaqs(faqData || []);
      } catch (error) {
        console.error('Error fetching page:', error);
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchPageAndFaqs();
  }, [slug, navigate]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Klantenservice | Buitn Planten</title>
        </Helmet>
        <ContactBar />
        <Header />
        <div className="flex min-h-screen items-center justify-center bg-white">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
        <Footer />
      </>
    );
  }

  if (!page) {
    return null;
  }

  return (
    <>
      <Helmet>
        <title>{page.meta_title || page.title} | Buitn Planten</title>
        <meta name="description" content={page.meta_description || page.title} />
        <link rel="canonical" href={`https://buitnplanten.nl/klantenservice/${page.slug}`} />

        {/* FAQPage Schema */}
        {faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer.replace(/<[^>]*>/g, '')
                }
              }))
            })}
          </script>
        )}
      </Helmet>

      <ContactBar />
      <Header />

      <main className="pt-8 pb-16 bg-white">
        <div className="max-w-7xl mx-auto px-8 sm:px-12 lg:px-16">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Klantenservice', href: '/klantenservice' }, { label: page.title }]} />
          <div className="text-center mb-16">
            <h1 className="text-5xl md:text-6xl font-bold text-jv-primary mb-6">
              {page.title}
            </h1>
            {page.description && (
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                {page.description}
              </p>
            )}
          </div>

          {slug === 'afhalen' && (
            <div className="group relative mb-8 overflow-hidden rounded-2xl border border-[#1e2b22] bg-[#1e2b22] p-6 text-center shadow-[0_12px_30px_rgba(30,43,34,0.14)] transition-shadow duration-300 hover:shadow-[0_16px_36px_rgba(30,43,34,0.22)] sm:p-8">
              <div className="pointer-events-none absolute -right-12 -top-16 h-40 w-40 rounded-full border border-[#d8e2d1]/20" />
              <div className="pointer-events-none absolute -bottom-24 -left-8 h-44 w-44 rounded-full border border-[#d8e2d1]/10" />
              <div className="relative flex flex-col items-center">
                <h2 className="text-2xl font-bold text-white sm:text-3xl">20% korting bij afhalen</h2>
                <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-[#e8eee4] sm:text-base">
                  Producten uit eigen kwekerij
                </p>
                <div className="mt-4 flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
                  <span className="text-sm text-[#e8eee4]">Kortingscode</span>
                  <span className="rounded bg-white/10 px-3 py-1.5 text-lg font-semibold tracking-wide text-white">AFHALEN20</span>
                </div>
                <a
                  href="/assortiment"
                  className="mt-6 inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#d8e2d1] px-6 py-3 text-sm font-semibold text-[#1e2b22] transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#d8e2d1] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1e2b22]"
                >
                  Bekijk assortiment
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </a>
              </div>
            </div>
          )}

          {(slug === 'bezorging' || slug === 'bezorgen') && (
            <div className="mb-8 rounded-2xl border border-[#e7e1d6] bg-white p-6 shadow-[0_2px_12px_rgba(35,38,35,0.04)] sm:p-8">
              <h2 className="mb-5 text-2xl font-bold text-jv-primary">Bezorgkosten in het kort</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-[#f2ede4] p-5">
                  <p className="text-sm font-semibold text-jv-primary">Tuinplanten</p>
                  <p className="mt-1 text-lg font-bold text-[#1e2b22]">€10,-</p>
                  <p className="text-sm text-gray-600">verzendkosten</p>
                </div>
                <div className="rounded-xl bg-[#f2ede4] p-5">
                  <p className="text-sm font-semibold text-jv-primary">Haagplanten en palletzendingen onder €795,-</p>
                  <p className="mt-1 text-lg font-bold text-[#1e2b22]">€49,95</p>
                  <p className="text-sm text-gray-600">bezorgkosten</p>
                </div>
                <div className="rounded-xl bg-[#1e2b22] p-5 text-white">
                  <p className="text-sm font-semibold text-[#d8e2d1]">Bestellingen vanaf €795,-</p>
                  <p className="mt-1 text-lg font-bold">Gratis</p>
                  <p className="text-sm text-[#e8eee4]">bezorging</p>
                </div>
                <div className="rounded-xl bg-[#f2ede4] p-5">
                  <p className="text-sm font-semibold text-jv-primary">Bomen</p>
                  <p className="mt-1 text-lg font-bold text-[#1e2b22]">Op aanvraag</p>
                  <p className="text-sm text-gray-600">neem contact op</p>
                </div>
              </div>
              <div className="mt-5 rounded-xl border border-[#d8e2d1] bg-[#1e2b22] p-5 text-center">
                <p className="text-sm text-[#e8eee4]">
                  Kies <strong className="font-semibold text-white">Ophalen</strong> tijdens het afrekenen en ontvang 20% korting met code
                  <strong className="mx-1.5 rounded bg-white/10 px-2 py-0.5 font-semibold tracking-wide text-white">AFHALEN20</strong>.
                </p>
              </div>
            </div>
          )}

          {faqs.length > 0 ? (
            <div className="rounded-2xl border border-[#e7e1d6] bg-white p-8 shadow-[0_2px_12px_rgba(35,38,35,0.04)]">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, index) => (
                  <AccordionItem key={faq.id} value={faq.id} className={index === faqs.length - 1 ? 'border-b-0' : ''}>
                    <AccordionTrigger className="text-left text-lg font-medium text-jv-primary hover:text-jv-accent">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="prose prose-sm max-w-none leading-relaxed text-gray-600 prose-a:font-medium prose-a:underline prose-p:mb-4 prose-ul:my-4 prose-ol:my-4 prose-li:my-1">
                      <div
                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(marked.parse(faq.answer) as string) }}
                        className="[&_a]:text-jv-primary [&_a]:underline [&_a:hover]:text-jv-accent"
                      />
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          ) : (
            <div className="rounded-2xl border border-[#e7e1d6] bg-white p-12 text-center shadow-[0_2px_12px_rgba(35,38,35,0.04)]">
              <p className="text-gray-600">
                Er is momenteel nog geen informatie beschikbaar voor deze pagina.
              </p>
            </div>
          )}

          <div className="mt-12 rounded-2xl border border-[#e7e1d6] bg-[#f2ede4] p-8 text-center">
            <h2 className="mb-4 text-2xl font-bold text-jv-primary">
              Nog niet gevonden wat je zoekt?
            </h2>
            <p className="mb-6 text-gray-600">
              Neem gerust contact met ons op voor persoonlijke hulp.
            </p>
            <div className="flex flex-col gap-4 justify-center sm:flex-row">
              <a
                href="tel:+31850233564"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-jv-primary px-6 py-3 font-medium text-white transition-colors hover:bg-jv-accent"
              >
                <Phone className="h-5 w-5" strokeWidth={1.6} />
                Bel ons
              </a>
              <a
                href="https://wa.me/31850233564"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#cfcac2] bg-white px-6 py-3 font-medium text-jv-primary transition-colors hover:border-jv-primary"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                </svg>
                WhatsApp met ons
              </a>
              <a
                href="mailto:info@buitnplanten.nl"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-[#cfcac2] bg-white px-6 py-3 font-medium text-jv-primary transition-colors hover:border-jv-primary"
              >
                <Mail className="h-5 w-5" strokeWidth={1.6} />
                Mail ons
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}
